import isEmpty from 'lodash/isEmpty';
import type { AirRevalidateItineraryResponse } from '../../types/api/v2/obt/model/air-revalidate-itinerary-response';
import { useGetFareChangeBreakupNodes } from '../../utils/Flights/useGetFareChangeBreakupNodes';
import { getTotalFareFromV2FareBreakdown } from '../../utils/Flights/getTotalFareFromV2FareBreakdown';
import type AirSelectedItineraryResponseManager from '../../services/AirSelectedItineraryResponseManager';
import { MoneyUtil } from '../../utils/Money';
import type { IFareComparison, IUseFareChangeDetailsResult } from './useFareChangeDetails';
import type { IFareBreakupFields } from '../../types/Flights/fareChangeBreakupNode';

interface IUseFareChangeDetailsV2 {
  revalidateResponse: AirRevalidateItineraryResponse | null;
  fareBreakup: IFareBreakupFields | null;
  airSelectedItineraryResponseManager?: AirSelectedItineraryResponseManager;
}

/**
 * Returns a model that is compatible with useGetFareChangeBreakupNodes
 */
const getRevalidateResponseFares = (revalidateResponse: AirRevalidateItineraryResponse | null) => {
  if (!revalidateResponse) {
    return null;
  }

  const { fareBreakDown, legPrices } = revalidateResponse;

  const updatedFareWithoutServiceFees = getTotalFareFromV2FareBreakdown(fareBreakDown);

  return {
    totalFare: updatedFareWithoutServiceFees.toMoneyType(),
    legPrices,
  };
};

/**
 * This hook returns fare change details with the v2 air-revalidate-itinerary-reponse from @spotnana/types
 */
export const useFareChangeDetailsV2 = ({
  revalidateResponse,
  fareBreakup,
  airSelectedItineraryResponseManager,
}: IUseFareChangeDetailsV2): IUseFareChangeDetailsResult | null => {
  const { breakup, isFareChangePositive } = useGetFareChangeBreakupNodes({
    revalidateResponse: getRevalidateResponseFares(revalidateResponse),
    fareBreakup,
  });

  if (!revalidateResponse || !airSelectedItineraryResponseManager) {
    return null;
  }

  const { legPrices } = revalidateResponse;

  const isVoidable = airSelectedItineraryResponseManager.isItineraryVoidable();

  const newFareComparisons: IFareComparison[] = [];
  const hasNewFare = legPrices && !isEmpty(legPrices);

  if (hasNewFare) {
    legPrices.forEach((leg) => {
      const oldLegInfo = airSelectedItineraryResponseManager.GetLegDetails({
        itinIndex: 0,
        legNumber: Number(leg.legId),
      });
      const oldFareUtas = oldLegInfo.flightDetails[0].utas;
      const flightDetails = oldLegInfo.flightDetails[0];
      const { departureAirport, arrivalAirport } = flightDetails;

      const newFareComparison: IFareComparison = {
        legIndex: Number(leg.legId),
        departureAirport,
        arrivalAirport,
        newFare: {
          utas: [],
          cabinType: oldLegInfo.flightDetails[0].cabinType,
        },
        oldFare: {
          utas: oldFareUtas,
          cabinType: oldLegInfo.flightDetails[0].cabinType,
        },
      };
      newFareComparisons.push(newFareComparison);
    });
  }

  return {
    isVoidable,
    unusedCreditsApplied: MoneyUtil.zeroMoney(),
    policyInfos: [],
    breakup,
    newFareComparisons,
    isFareChangePositive,
  };
};
